/**
 * What the check-in card remembers on this device.
 *
 * - **Put off** ("Agora não"): the session ids the person dismissed. Stored in
 *   Capacitor Preferences under `mypocketgym.today.checkin-dismissed`, because
 *   T12's prefs store holds the theme only and exposes no generic key. At
 *   most {@link KEEP} ids are kept: the server offers a check-in for 48 h
 *   (T38), so an old id is never asked about again.
 * - **Answered**: the session ids answered in this run, so the question hides
 *   on the first 2xx (or the offline pause) before `GET /me/pending`
 *   refetches, and `sent` — the last answer, whose note the card shows in
 *   its place until closed. Memory only: the server's `checkIn: null` is the
 *   lasting answer.
 * - **Requested**: the session id the insight's `open_checkin` asked to show,
 *   which also brings back a dismissed card.
 *
 * A read or write failure is "nothing remembered", never an error: the worst
 * case is the card showing once more.
 */
import { useSyncExternalStore } from "react";
import { preferences } from "../../native";
import type { SoreArea, SorenessLevel } from "./model";

const KEY = "mypocketgym.today.checkin-dismissed";
const KEEP = 10;

export interface CheckinAnswer {
  readonly area: SoreArea | null;
  readonly level: SorenessLevel;
  readonly sessionId: string;
}

export interface CheckinState {
  readonly answered: readonly string[];
  readonly dismissed: readonly string[];
  readonly requested: string | null;
  readonly sent: CheckinAnswer | null;
}

let state: CheckinState = {
  answered: [],
  dismissed: [],
  requested: null,
  sent: null,
};
let hydrated = false;
/**
 * Bumped by {@link forgetCheckinState}. A read that was already in flight when
 * the account changed carries the PREVIOUS person's ids, and merging them back
 * in is exactly what the cleanup was for, so it is dropped by its generation.
 */
let generation = 0;
const listeners = new Set<() => void>();

function set(next: CheckinState): void {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

function hydrate(): void {
  if (hydrated) {
    return;
  }
  hydrated = true;
  const reading = generation;
  preferences
    .get(KEY)
    .then((raw) => {
      const parsed: unknown = raw === null ? [] : JSON.parse(raw);
      if (Array.isArray(parsed) && reading === generation) {
        const ids = parsed.filter((id): id is string => typeof id === "string");
        set({
          ...state,
          dismissed: [...new Set([...ids, ...state.dismissed])],
        });
      }
    })
    .catch(() => undefined);
}

function subscribe(listener: () => void): () => void {
  hydrate();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => state;

export function useCheckinState(): CheckinState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function dismissCheckin(sessionId: string): void {
  const dismissed = [
    ...state.dismissed.filter((id) => id !== sessionId),
    sessionId,
  ].slice(-KEEP);
  set({
    ...state,
    dismissed,
    requested: state.requested === sessionId ? null : state.requested,
  });
  preferences.set(KEY, JSON.stringify(dismissed)).catch(() => undefined);
}

export function markCheckinAnswered(answer: CheckinAnswer): void {
  const { sessionId } = answer;
  set({
    ...state,
    answered: [...state.answered, sessionId],
    requested: state.requested === sessionId ? null : state.requested,
    sent: answer,
  });
}

/** Closes the after-answer note. */
export function closeCheckinNote(): void {
  set({ ...state, sent: null });
}

/** The insight asked for it: show it again, even if it was put off. */
export function requestCheckin(sessionId: string): void {
  const dismissed = state.dismissed.filter((id) => id !== sessionId);
  set({ ...state, dismissed, requested: sessionId });
  preferences.set(KEY, JSON.stringify(dismissed)).catch(() => undefined);
}

/**
 * Forgets everything this device remembered for the account that just left:
 * the stored ids and the in-memory answers (T35's cleanup, `data/account-cleanup.ts`).
 *
 * The key is removed rather than written empty, and hydration is marked done:
 * from here memory IS the truth, so a later `subscribe()` cannot read the
 * previous person's ids back in. A failed removal rejects — the cleanup runner
 * reports it by name rather than swallowing it, unlike the reads above, where
 * "nothing remembered" is a safe answer.
 */
export async function forgetCheckinState(): Promise<void> {
  generation += 1;
  hydrated = true;
  set({ answered: [], dismissed: [], requested: null, sent: null });
  await preferences.remove(KEY);
}
